// shared-lib/cabinet-runs/mergeResolvedRuns.js
//
// After the fill: what the room's objects become. ONE sweep for every site
// (proposal §7) — the web's `objectsAfterRunsResolve` and the server's
// kept-filter were the same operation with two allowlists. What a run owns it
// re-emits every solve (§6: `runId` ⇒ emitted), so an owned object the fill
// did not emit is gone — with the exceptions the POLICY names (uppers in keep
// mode) and an oven column part the fill set aside this solve, which nothing
// can rebuild in its place (`isOvenColumnPart`, below).
//
// Order is preserved: kept objects stay in their room order, the emitted
// list follows. Incumbents (Task 4) are replaced IN PLACE.
'use strict';

const { baysForRun, freestandingApplianceClaims } = require('./bays');
const { countertopRunId } = require('../kitchen-assembly/cabinetLayout/toRuns');
const { moveApplianceObjectsToBays } = require('./moveAppliances');
const { reparentOntoCountertops } = require('./reparent');
const { runAliases } = require('./runIds');
const { policyFor } = require('./resolvePolicy');
const {
  isUpperTier,
  ownershipViolations,
  stampApplianceBays,
} = require('./ownership');
const { isOvenColumnPart } = require('./ovenColumn');
const { BASE_HEIGHT, BASE_HEIGHT_TOLERANCE } = require('./runDepth');
const { fitVanityBasinsToCounter } = require('./vanityBasinSeat');
const { followCounterHeight } = require('./vanityCounterFollow');
const {
  reseatSinkHosts,
  emitTallUnits,
  emitOvenColumns,
} = require('./incumbents');

const IN = 0.0254;
const short = (id) => String(id).slice(0, 8);

// Taller than an ordinary bay can be. `isBaseHeight` is a BAND — it answers
// false below 28.5" as well as above 40.5" — and the corpus carries three
// 18-20" run-owned bodies that are swept and re-emitted as modules exactly as
// intended. Only the upper edge is the tall-body question.
const TALL_BODY_MIN_HEIGHT = BASE_HEIGHT + BASE_HEIGHT_TOLERANCE;

/**
 * One `{ resolve, policy }` per resolve in the batch. The policy is resolved
 * ONCE here — `keepRule` is asked about every prior object and the incumbent
 * pass needs the same answer, and neither should re-derive it.
 */
function resolveOwners(resolves, site) {
  return (resolves || [])
    .filter((r) => r?.run)
    .map((r) => ({ resolve: r, policy: policyFor(site, r.run) }));
}

/** runId (own id and per-wall aliases) → the owner entry that answers for it. */
function ownersByRunId(owners) {
  const byId = new Map();
  for (const owner of owners) {
    const run = owner.resolve.run;
    if (!run.id) continue;
    byId.set(run.id, owner);
    for (const a of runAliases(run)) byId.set(a, owner);
  }
  return byId;
}

/**
 * Keep this prior object through the sweep? Owned by a run in this merge →
 * only if the policy keeps its tier; otherwise it is re-emitted or gone.
 * Unowned → kept, except a legacy untagged slab over owned cabinets.
 */
function keepRule(o, owners, byId) {
  if (o?.runId) {
    const owner = owners.get(o.runId);
    if (!owner) return true;
    if (owner.policy.uppers === 'keep' && isUpperTier(o)) return true;
    // A column section the fill did not claim this solve — its bay was
    // `unplaced`/`dangling` and so never reached `resolve.bays` — is never
    // rebuilt by anything else: `emitOvenColumns` only re-emits a CLAIMED
    // bay's sections, and there is no other producer of a base cabinet, a
    // surround, or an over-oven cabinet. Never delete what you cannot
    // rebuild. A part the pass DID claim this solve is already kept via
    // `replacements` above the caller's filter, regardless of this
    // function's answer — so this arm only ever fires for the set-aside
    // case, where keeping is right (and `sweptTallBodyWarnings` below is the
    // backstop that would report it if this arm were ever wrong).
    return isOvenColumnPart(o);
  }
  if (o?.type !== 'countertop') return true;
  return !owners.has(countertopRunId(o, byId));
}

/**
 * The run OWNS the position of the appliances in its bays: each resolve
 * moves its appliance objects to their solved centres, then the site's
 * settle hook honours their fixture-group links (layout-relationships §2.5).
 *
 * Takes the hoisted `{ resolve, policy }` owners rather than the raw resolves:
 * the policy is already resolved once per run above and must not be re-derived
 * per pass.
 */
function applyApplianceMoves(room, merged, owners, ctx) {
  let objects = merged;
  const moved = [];
  // A freestanding run's claim outranks a wall run's: a WALL resolve must
  // never reseat an island's appliance, even off a stale phantom bay the heal
  // hasn't reached yet (belt for the 2026-08-31 island-drag bug).
  const freeClaims = freestandingApplianceClaims(room?.cabinetRuns);
  for (const { resolve: r, policy } of owners) {
    if (!r?.bays?.length && !r?.freeSlidBays?.length) continue;
    if (!policy.moveAppliances) continue;
    const runIsFree = (r.run.segments || []).some((s) => s?.freestanding);
    const guarded =
      runIsFree || !freeClaims.size
        ? r
        : {
            ...r,
            bays: r.bays.filter((sb) => {
              const bay = baysForRun(r.run).find((b) => b.id === sb.bayId);
              return !(bay?.applianceId && freeClaims.has(bay.applianceId));
            }),
          };
    const res = moveApplianceObjectsToBays(objects, guarded.run, guarded, ctx);
    objects = res.objects;
    moved.push(...res.moved);
  }
  if (!moved.length || !ctx.settleGroups) return { objects, moved };
  const settled = ctx.settleGroups({ ...room, objects }, new Set(moved));
  return { objects: settled.room.objects, moved };
}

/**
 * A TALL body the sweep took that nothing puts back.
 *
 * `scanApplianceAnchors` reserves a tall `lower-cabinet` as a `tallUnit` bay
 * only when it STANDS ON THE FLOOR — a pantry hung above it is "an ordinary
 * bay — filled, not reserved". No bay names it, so `emitTallUnits` never
 * re-seats it, and since the run owns it the sweep takes it while the fill
 * re-emits a 34.5" module in its place: a floor-to-ceiling body replaced by a
 * base cabinet, with nothing said. Before the branch a blanket exemption kept
 * every tall body; the exemption is gone on purpose (a tall unit is re-emitted
 * from its bay now), so this is the tripwire for the case the bays miss.
 *
 * A `category.tall` box or a `category.filler` strip is run-emitted (proposal
 * §9 step 6) and exempt; the tripwire is for MEASURED bodies the bays missed.
 *
 * REPORT ONLY — the sweep is unchanged. Measured against what SURVIVED rather
 * than against the replacement/emitted lists alone, so an incumbent
 * re-seated in place and a body `keepRule` exempts are both silent — a
 * column section `emitOvenColumns` re-emits is the first of those, and a
 * SET-ASIDE column section `keepRule` spares outright is the second; King's
 * Drive's base (F6D83996) measures 43.1", above the band this docstring says
 * a measured body usually sits inside.
 *
 * @param {Object[]} prior         the room's objects before the solve
 * @param {Map} owners             runId → the owner entry that answers for it
 * @param {Set} survivingIds       the ids in the merged output
 */
function sweptTallBodyWarnings(prior, owners, survivingIds) {
  const out = [];
  for (const o of prior) {
    if (!o?.id || !o.runId || o.type !== 'lower-cabinet') continue;
    // A run-EMITTED box is exempt: a tall span's module (`category.tall`) and
    // any filler (`category.filler` — a span's remainder is a Tall Filler at
    // 84", rebuilt every solve too). Both are swept and re-emitted by design,
    // and a slid span mints new content-keyed ids. No measured body carries
    // either flag.
    if (o.category?.tall || o.category?.filler) continue;
    if (!owners.has(o.runId)) continue;
    const h = o.dimensions?.y;
    if (!Number.isFinite(h) || h <= TALL_BODY_MIN_HEIGHT) continue;
    if (survivingIds.has(o.id)) continue;
    out.push(
      `tall body ${short(o.id)} (${(h / IN).toFixed(1)}" tall) was swept by ` +
        `run ${short(o.runId)} and nothing re-emits it`
    );
  }
  return out;
}

/**
 * One line per contract violation, for the site's warnings list.
 *
 * RUN ids print in FULL: a derived run is named for its first wall
 * (`scanrun_<wallId>`), so eight characters is `scanrun_` and names nothing.
 * OBJECT ids keep the slice — they are UUIDs, and eight characters identify
 * one in a room. A `run-unreadable` violation carries the run in `v.id`.
 */
function ownershipWarning(v) {
  const id = v.type === 'run' ? String(v.id) : short(v.id);
  const where = v.runId ? ` (run ${String(v.runId)})` : '';
  const why = v.message ? `: ${v.message}` : '';
  return `ownership: ${v.rule} — ${v.type} ${id}${where}${why}`;
}

/**
 * @param {Object} room       the room BEFORE the solve (objects = incumbents)
 * @param {Object} input
 * @param {Object[]} input.resolves  `materializeRun` results (`run`, `bays`,
 *   `removedObjectIds`); one per run in the batch
 * @param {Object[]} input.emitted   the objects to write — normally the
 *   concatenation of the resolves' `objects`, but a site may add to or restyle
 *   them first (an appliance drop, a room restyle)
 * @param {'edit'|'scan'} site
 * @param {Object} [ctx]  { step?, settleGroups?, now?, prevWallsById? }
 * @returns {{ objects, moved, warnings, violations }}
 */
function mergeResolvedRuns(room, { resolves, emitted }, site, ctx = {}) {
  const prior = room?.objects || [];
  const list = resolves || [];
  const owned = resolveOwners(list, site);
  const owners = ownersByRunId(owned);
  const byId = new Map(prior.filter((o) => o?.id).map((o) => [o.id, o]));
  const warnings = [];
  const dropped = new Set();
  for (const r of list)
    for (const id of r?.removedObjectIds || []) dropped.add(id);

  // Incumbents first: the sink host takes the stand-in's span, the tall unit
  // follows its bay. Both are replaced IN PLACE in the kept list below, so
  // room order is preserved; the stand-in base leaves the emitted list.
  const replacements = new Map();
  const reseat = reseatSinkHosts({
    emitted: emitted || [],
    objects: prior,
    warnings,
    step: ctx.step,
  });
  const toEmit = reseat.emitted;
  for (const [id, o] of reseat.replacements) replacements.set(id, o);
  for (const { resolve, policy } of owned) {
    const tall = emitTallUnits({
      resolve,
      objects: prior,
      warnings,
      step: ctx.step,
      policy,
    });
    for (const [id, o] of tall.replacements) replacements.set(id, o);
    // An oven column is the same rule over a SET of incumbents; the two
    // passes are disjoint by `isReseatedTallUnit` and can never claim one bay.
    const column = emitOvenColumns({
      resolve,
      objects: prior,
      warnings,
      step: ctx.step,
      policy,
    });
    for (const [id, o] of column.replacements) replacements.set(id, o);
  }

  const fresh = new Set(toEmit.map((o) => o?.id).filter(Boolean));
  // Each replacement is written ONCE. A room saved with two objects under a
  // host's id (before `remintTakenId`) matched it twice, and both became the
  // host; the second is the stale box the fill has re-emitted under a new id.
  const placed = new Set();
  const kept = prior
    .filter(
      (o) =>
        // A resolve that REMOVED an object outranks an incumbent replacement:
        // the run is entitled to delete what it owns, and a stand-in seated on
        // a removed object would carry a deleted body back into the room.
        !dropped.has(o?.id) &&
        (replacements.has(o?.id)
          ? !placed.has(o.id) && placed.add(o.id)
          : keepRule(o, owners, byId) && !fresh.has(o?.id))
    )
    .map((o) => replacements.get(o.id) || o);
  const merged = reparentOntoCountertops([...kept, ...toEmit]);
  warnings.push(
    ...sweptTallBodyWarnings(
      prior,
      owners,
      new Set(merged.map((o) => o?.id).filter(Boolean))
    )
  );
  const horizontal = applyApplianceMoves(room, merged, owned, ctx);
  // …and then VERTICALLY: a vanity's basin hangs from the counter the fill
  // just rebuilt, and the moves above are along the wall and across it only
  // (`vanityBasinSeat.js` — a converted 30 ¼" vanity left its bowl 1 ¾" under
  // the slab). Per run, because each owns its own counter.
  let objects = horizontal.objects;
  const moved = [...horizontal.moved];
  for (const { resolve: r } of owned) {
    if (!r?.run) continue;
    const seated = fitVanityBasinsToCounter(objects, r.run, ctx);
    objects = seated.objects;
    moved.push(...seated.moved);
    // …and what hangs from that counter — the faucet, the mirror, the lights
    // over it — moves up or down with it (`vanityCounterFollow.js`): a family
    // switch changes a vanity run's body height, and the faucet was left
    // floating over the counter or sunk into it.
    const followed = followCounterHeight(objects, prior, r.run, ctx);
    objects = followed.objects;
    moved.push(...followed.moved);
  }

  // The runs the contract is measured against: the room's PERSISTED runs, plus
  // every run in this batch the room does not carry yet. A caller whose runs
  // are DERIVED (the scan materializer) must put them on the room it hands in,
  // or a run it kept as scanned — one with no resolve at all — reads as a tag
  // naming no run on every object it owns.
  const runsForContract = [
    ...(room?.cabinetRuns || []),
    ...list
      .map((r) => r?.run)
      .filter(
        (r) => r?.id && !(room?.cabinetRuns || []).some((s) => s?.id === r.id)
      ),
  ];
  const stamped = stampApplianceBays(objects, runsForContract);
  const violations = ownershipViolations(stamped, runsForContract);
  for (const v of violations) warnings.push(ownershipWarning(v));
  return { objects: stamped, moved, warnings, violations };
}

module.exports = { mergeResolvedRuns, keepRule, isUpperTier };
